import { useId, useRef, useState, type ReactNode } from 'react';
import { Notification, TickCircle } from 'iconsax-reactjs';
import {
  Button,
  DateTimeDisplay,
  EmptyState,
  IconButton,
  Popover,
  Skeleton,
  cx,
} from '@/components/ui';
import styles from './NotificationCenter.module.css';

export interface NotificationItem {
  id: string;
  title: string;
  body?: string;
  /** ISO-8601 UTC instant. */
  createdAt: string;
  /** ISO-8601 UTC instant; null/undefined means unread. */
  readAt?: string | null;
  href?: string;
}

export interface NotificationCenterProps {
  notifications: NotificationItem[];
  loading?: boolean;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  /** Called when a notification with an href is activated (e.g. navigate). */
  onOpenNotification?: (notification: NotificationItem) => void;
  /**
   * Render the title as a router link (spread `props` on it). Defaults to <a href> when `href`
   * is present. `props.onClick` marks the notification read and closes the panel.
   */
  renderLink?: (
    notification: NotificationItem & { href: string },
    props: { className: string; onClick: () => void },
    children: ReactNode,
  ) => ReactNode;
  /** Server-side unread total when the list is paginated. Defaults to the unread items given. */
  unreadCount?: number;
  onOpenChange?: (open: boolean) => void;
  /** Optional footer (e.g. a "View all" link). A function receives `close` for links. */
  footer?: ReactNode | ((close: () => void) => ReactNode);
  /**
   * Optional content between the header and the list (e.g. unread counts per category). A
   * function receives `close` for links.
   */
  summary?: ReactNode | ((close: () => void) => ReactNode);
  title?: string;
}

function formatCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/** Bell button with unread badge that opens a notifications panel. Data arrives via props. */
export function NotificationCenter({
  notifications,
  loading = false,
  onMarkRead,
  onMarkAllRead,
  onOpenNotification,
  renderLink,
  unreadCount,
  onOpenChange,
  footer,
  summary,
  title = 'Notifications',
}: NotificationCenterProps) {
  const headingId = useId();
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const unread = unreadCount ?? notifications.filter((n) => !n.readAt).length;

  function setOpenState(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  function activate(notification: NotificationItem) {
    if (!notification.readAt) onMarkRead(notification.id);
    onOpenNotification?.(notification);
    setOpenState(false);
  }

  return (
    <>
      <IconButton
        ref={bellRef}
        label={unread > 0 ? `${title}, ${unread} unread` : title}
        tooltip={title}
        icon={<Notification size={20} color="currentColor" />}
        badge={unread > 0 ? formatCount(unread) : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpenState(!open)}
      />
      <Popover
        open={open}
        onClose={() => setOpenState(false)}
        anchorRef={bellRef}
        role="dialog"
        aria-labelledby={headingId}
        align="end"
        className={styles.panel}
      >
        <div className={styles.header}>
          <h2 id={headingId} className={styles.title}>
            {title}
          </h2>
          {unread > 0 && (
            <Button
              variant="link"
              size="sm"
              leftIcon={<TickCircle size={14} color="currentColor" />}
              onClick={onMarkAllRead}
            >
              Mark all as read
            </Button>
          )}
        </div>
        {summary && (typeof summary === 'function' ? summary(() => setOpenState(false)) : summary)}
        <div className={styles.body} aria-busy={loading || undefined}>
          {loading ? (
            <ul className={styles.list} aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <li key={i} className={styles.item}>
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="30%" />
                </li>
              ))}
            </ul>
          ) : notifications.length === 0 ? (
            <EmptyState
              size="sm"
              titleAs="p"
              icon={<Notification size={24} color="currentColor" />}
              title="You're all caught up"
              description="New notifications will appear here."
            />
          ) : (
            <ul className={styles.list}>
              {notifications.map((notification) => {
                const isUnread = !notification.readAt;
                const titleContent = notification.title;
                let titleNode: ReactNode = <span className={styles.itemTitle}>{titleContent}</span>;
                if (notification.href) {
                  const linkProps = {
                    className: cx(styles.itemLink),
                    onClick: () => activate(notification),
                  };
                  titleNode = (
                    <span className={styles.itemTitle}>
                      {renderLink ? (
                        renderLink(
                          { ...notification, href: notification.href },
                          linkProps,
                          titleContent,
                        )
                      ) : (
                        <a href={notification.href} {...linkProps}>
                          {titleContent}
                        </a>
                      )}
                    </span>
                  );
                }
                return (
                  <li key={notification.id} className={cx(styles.item, isUnread && styles.unread)}>
                    <div className={styles.itemMain}>
                      {isUnread && <span className={styles.dot} aria-hidden="true" />}
                      <div className={styles.itemText}>
                        {isUnread && <span className="sr-only">Unread: </span>}
                        {titleNode}
                        {notification.body && (
                          <p className={styles.itemBody}>{notification.body}</p>
                        )}
                        <DateTimeDisplay
                          value={notification.createdAt}
                          style="relative"
                          className={styles.itemTime}
                        />
                      </div>
                    </div>
                    {isUnread && (
                      <button
                        type="button"
                        className={styles.markRead}
                        onClick={() => onMarkRead(notification.id)}
                      >
                        Mark as read
                        <span className="sr-only">: {notification.title}</span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {footer && (
          <div className={styles.footer}>
            {typeof footer === 'function' ? footer(() => setOpenState(false)) : footer}
          </div>
        )}
      </Popover>
    </>
  );
}
